/**
 * Lesson Checklist — right-side collapsible panel listing tasks for a
 * checklist-mode enterprise lesson (ent-gfx-03). Any order; all must
 * complete to finish the lesson.
 */
import type { EnterpriseTask } from '@graphicsim/shared-types';
import { enterpriseStore } from '../stores/enterpriseStore';
import { reopenLessonVideoModal } from './lessonVideoModal';

let panel: HTMLElement | null = null;
let unsubscribe: (() => void) | null = null;
let hasTutorialVideo = false;

export function renderLessonChecklist(
  tasks: EnterpriseTask[],
  opts: { hasTutorialVideo?: boolean } = {},
): void {
  destroyLessonChecklist();
  hasTutorialVideo = !!opts.hasTutorialVideo;

  panel = document.createElement('aside');
  panel.className = 'lesson-checklist';
  panel.innerHTML = `
    <button class="lesson-checklist-toggle" title="Toggle checklist">&rsaquo;</button>
    <div class="lesson-checklist-body">
      <header class="lesson-checklist-header">
        <h3>Lesson Tasks</h3>
        <span class="lesson-checklist-count"></span>
      </header>
      ${hasTutorialVideo ? `
      <button class="lesson-checklist-video-link">&#9654; Rewatch Tutorial</button>
      ` : ''}
      <ul class="lesson-checklist-items"></ul>
    </div>
  `;

  panel.querySelector('.lesson-checklist-toggle')!.addEventListener('click', () => {
    panel!.classList.toggle('collapsed');
  });

  if (hasTutorialVideo) {
    panel.querySelector('.lesson-checklist-video-link')!.addEventListener('click', () => {
      reopenLessonVideoModal();
    });
  }

  document.body.appendChild(panel);

  paintList(tasks);

  unsubscribe = enterpriseStore.subscribe(() => paintList(tasks));
}

function paintList(tasks: EnterpriseTask[]): void {
  if (!panel) return;
  const completed = new Set(enterpriseStore.getState().completedStepIds);
  const list = panel.querySelector<HTMLUListElement>('.lesson-checklist-items')!;
  const count = panel.querySelector<HTMLSpanElement>('.lesson-checklist-count')!;
  count.textContent = `${completed.size} / ${tasks.length}`;

  list.innerHTML = tasks.map((t, i) => {
    const done = completed.has(t.id);
    return `
      <li class="lesson-checklist-item ${done ? 'done' : ''}" data-task-id="${t.id}">
        <span class="lesson-checklist-check">${done ? '&#10003;' : String(i + 1)}</span>
        <div class="lesson-checklist-text">
          <div class="lesson-checklist-title">${t.title}</div>
          <div class="lesson-checklist-instruction">${t.instruction}</div>
        </div>
      </li>
    `;
  }).join('');
}

export function destroyLessonChecklist(): void {
  if (unsubscribe) { unsubscribe(); unsubscribe = null; }
  if (panel) { panel.remove(); panel = null; }
  hasTutorialVideo = false;
}
